import React from 'react';
import { Box, Typography } from '@mui/material';

import { DataAPIContext, GET_ONE } from '../api';
import { usePageVisibility } from '../components/usePageVisibility';

const LOG_POLL_INTERVAL = 3000;

const TaskLogViewer = React.forwardRef(({ taskId, taskState, logKey }, ref) => {
    const dataAPI = React.useContext(DataAPIContext);
    const isPageVisible = usePageVisibility();
    const [entries, setEntries] = React.useState([]);
    const [, setSource] = React.useState(null);
    const containerRef = React.useRef(null);
    const pollRef = React.useRef(null);
    // Cursor into the live Redis log stream: the id of the newest entry we
    // already hold. Sent back as ?after= so each poll only transfers the new
    // lines instead of the whole log (which grows for the length of the run).
    const lastIdRef = React.useRef(null);
    // Ids already rendered — guards against double-appending when two reads
    // overlap (StrictMode's double mount, or a slow response arriving late).
    const seenIdsRef = React.useRef(new Set());

    const isRunning = taskState === 0 || taskState === 1;
    const isTerminal = taskState === 2 || taskState === 3 || taskState === 4;

    // Switching to another task starts from scratch
    React.useEffect(() => {
        lastIdRef.current = null;
        seenIdsRef.current = new Set();
        setEntries([]);
        setSource(null);
    }, [taskId]);

    const fetchLogs = React.useCallback(() => {
        if (!taskId) return;
        const after = lastIdRef.current;
        const suffix = after
            ? `${taskId}/logs/?after=${encodeURIComponent(after)}`
            : `${taskId}/logs/`;
        dataAPI(GET_ONE, 'bg_tasks', { urlSuffix: suffix }).then((res) => {
            if (!res || res.statusCode !== 200 || !res.data) return;
            setSource(res.data.source);
            const incoming = res.data.entries || [];
            if (res.data.source === 'archive') {
                // The S3 archive is the complete log — replace, never append.
                lastIdRef.current = null;
                seenIdsRef.current = new Set();
                setEntries(incoming);
                return;
            }
            if (!res.data.last_id) {
                // No cursor offered (empty stream, or a backend that predates
                // ?after=) — the response is the whole log, so replace.
                seenIdsRef.current = new Set();
                setEntries(incoming);
                return;
            }
            // Live: only entries newer than the cursor come back.
            const fresh = incoming.filter((e) => !e.id || !seenIdsRef.current.has(e.id));
            fresh.forEach((e) => { if (e.id) seenIdsRef.current.add(e.id); });
            lastIdRef.current = res.data.last_id;
            if (fresh.length > 0) setEntries((prev) => [...prev, ...fresh]);
        }).catch(() => {});
    }, [dataAPI, taskId]);

    // Fetch on mount and on every state change. On the terminal transition the
    // cursor is dropped so the final read picks up the tail of the run — and
    // the archive, once the live stream has been cleaned up; anything already
    // rendered is filtered out by stream id.
    React.useEffect(() => {
        if (isTerminal) lastIdRef.current = null;
        fetchLogs();
    }, [fetchLogs, taskState, isTerminal]);

    // Poll while task is running
    React.useEffect(() => {
        if (isRunning && isPageVisible) {
            pollRef.current = setInterval(fetchLogs, LOG_POLL_INTERVAL);
        }
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [isRunning, isPageVisible, fetchLogs]);

    // Auto-scroll to bottom
    React.useEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    }, [entries]);

    // Expose entries to parent via ref — must be before any early return
    React.useImperativeHandle(ref, () => ({
        getLogText: () =>
            entries.map(e => `${e.ts || ''}\t${e.levelname || ''}\t${e.msg || JSON.stringify(e)}`).join('\n'),
        getEntries: () => entries,
        hasEntries: () => entries.length > 0,
    }), [entries]);

    if (entries.length === 0) {
        return (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                No log entries available
            </Typography>
        );
    }

    return (
        <Box
            ref={containerRef}
            sx={{
                maxHeight: 400,
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                lineHeight: 1.6,
                p: 1,
                bgcolor: 'grey.50',
                borderRadius: 1,
            }}
        >
            {entries.map((entry, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, '&:hover': { bgcolor: 'grey.100' } }}>
                    <Typography
                        component="span"
                        sx={{ fontFamily: 'monospace', fontSize: 'inherit', color: 'text.secondary', whiteSpace: 'nowrap' }}
                    >
                        {entry.ts || ''}
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: 'monospace',
                            fontSize: 'inherit',
                            color: entry.levelname === 'ERROR' || entry.levelname === 'CRITICAL'
                                ? 'error.main'
                                : entry.levelname === 'WARNING'
                                ? 'warning.main'
                                : 'text.primary',
                        }}
                    >
                        {entry.msg || JSON.stringify(entry)}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
});

TaskLogViewer.displayName = 'TaskLogViewer';

export default TaskLogViewer;
